import { RequestType, RequestTypeDisplayTextMap } from '@strata/react-chat';
import { BaseNotification, type NotificationBaseProps } from '@strata/react-notifications';
import { type ComponentProps } from 'react';
import { useNavigate } from 'react-router';

import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';

export interface BasePayerRequestAdditonalData {
    requestNumber: number;
    requestType: (typeof RequestType)[keyof typeof RequestType];
    requestSubType?: string;
    additionalNotes?: string;
}

export interface CommonPayerRequestNotificationProps
    extends
        NotificationBaseProps,
        Pick<ComponentProps<typeof BaseNotification>, 'icon' | 'timestamp'> {
    linkingId: string;
    additionalData: BasePayerRequestAdditonalData;
    payerId: string;
}

export interface BasePayerRequestNotificationProps
    extends
        CommonPayerRequestNotificationProps,
        Pick<ComponentProps<typeof BaseNotification>, 'notificationTitle'> {
    additionalDataComponent: React.ReactNode;
}

export const BasePayerRequestNotification = ({
    linkingId,
    timestamp,
    additionalData,
    onNotificationClick,
    onRemoveNotificationClick,
    status,
    payerId,
    additionalDataComponent,
    notificationTitle,
    icon,
}: BasePayerRequestNotificationProps) => {
    const navigate = useNavigate();
    const { payerId: userPayerId } = useAppSelector((state) => state.auth);

    const getBodyText = () => {
        let secondaryText: string | undefined = '';
        if (
            (
                [
                    RequestType.ESCALATION,
                    RequestType.CHANGE,
                ] as (typeof RequestType)[keyof typeof RequestType][]
            ).includes(additionalData.requestType)
        ) {
            secondaryText = additionalData.requestSubType;
        }
        if (additionalData.requestType === RequestType.OTHER) {
            secondaryText = additionalData.additionalNotes;
        }
        return `${RequestTypeDisplayTextMap[additionalData.requestType]} | ${secondaryText}`;
    };

    return (
        <BaseNotification
            bodyText={getBodyText()}
            onNotificationClick={() => {
                onNotificationClick();
                if (payerId === userPayerId) {
                    navigate(AppRoutes.requestDetails(linkingId));
                } else {
                    window.open(AppRoutes.requestDetailsWithPayer(linkingId, payerId), '_blank');
                }
            }}
            type={BaseNotification.Types.INFO}
            notificationTitle={notificationTitle}
            icon={icon}
            timestamp={timestamp}
            onRemoveNotificationClick={onRemoveNotificationClick}
            status={status}
            additionalData={additionalDataComponent}
        />
    );
};
